import moment, { Moment } from "moment-timezone";
import { useEffect, useState, useRef, useCallback, ElementType } from "react";
import { Tooltip, Box, keyframes } from "@mui/material";
import {
  IconMoodHappy,
  IconMoodPlus,
  IconMoodPuzzled,
  IconMoodSmile,
  IconMoodSmileBeam,
} from "@tabler/icons-react";
import { LuBicepsFlexed } from "react-icons/lu";

import TimeUtils from "utils/time";
import CurrentSession from "data/userSession";

// ============================================================================
// LastTugged State Calculation Logic
// ============================================================================

interface LastTuggedState {
  color: string;
  displayString: string;
  active: boolean;
}

/**
 * Calculate the state for LastTugged component based on date and conditions
 */
function calculateLastTuggedState(
  date: Moment | Date | string | null | undefined,
  timezone: string | null = null,
  inProgress: boolean = false,
  isCurrentUser: boolean = false,
  maxUnits: number = 2
): LastTuggedState {
  // Active/in-progress always takes priority
  if (inProgress) {
    return {
      color: "teal",
      displayString: "Active",
      active: true,
    };
  }

  // Handle null/undefined dates
  if (!date) {
    return {
      color: "info",
      displayString: "New User!",
      active: false,
    };
  }

  const dateToCompare = TimeUtils.inThisTimezone(date, timezone);
  const dateMoment = moment(dateToCompare);
  const now = moment();

  // Check if future date (e.g. timezone edge case)
  if (dateMoment.isAfter(now)) {
    return {
      color: "teal",
      displayString: "Active",
      active: true,
    };
  }

  const elapsed = now.diff(dateMoment);

  // Handle invalid elapsed time
  if (isNaN(elapsed)) {
    return {
      color: "info",
      displayString: "New User!",
      active: false,
    };
  }

  // Calculate display string
  const breakdown = TimeUtils.breakdown(elapsed);
  const display = TimeUtils.elapsedToString(breakdown, maxUnits);

  let displayString: string;
  if (!breakdown) {
    displayString = "New User!";
  } else if (display === "0") {
    displayString = isCurrentUser ? "Great!" : "Just Tugged!";
  } else {
    displayString = display;
  }

  // Determine color based on elapsed time thresholds
  const threeDays = moment.duration(3, "days").asMilliseconds();
  const oneDay = moment.duration(1, "days").asMilliseconds();
  const sixHours = moment.duration(6, "hours").asMilliseconds();

  let color: string;
  if (elapsed > threeDays) {
    color = "error";
  } else if (elapsed > oneDay) {
    color = "warning";
  } else if (elapsed > sixHours) {
    color = "success";
  } else if (elapsed && date) {
    color = "success";
  } else {
    color = "info";
  }

  return {
    color,
    displayString,
    active: false,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomChip: any = require("../../components/Chip").default;

export interface LastTuggedProps {
  date?: Moment | Date | string;
  style?: React.CSSProperties | null;
  isCurrentUser?: boolean;
  timezone?: string | null;
  inProgress?: boolean;
  // Units shown in the elapsed label. 2 -> "Xh MMm"; 1 -> "Xh" (condensed for
  // tight spaces like the header on small screens).
  maxUnits?: number;
}

const LastTugged = ({
  date,
  style = null,
  isCurrentUser = false,
  timezone = null,
  inProgress: inProgressProp = false,
  maxUnits = 2,
}: LastTuggedProps) => {
  const [color, setColor] = useState<string>("info");
  const [displayString, setDisplayString] = useState("New User!");
  const [Icon, setIcon] = useState<ElementType>(IconMoodPlus);
  const [active, setActive] = useState(inProgressProp);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const calculateImmediate = useCallback(
    function () {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let dateToCompare: any = date;
      // For current user, pull directly from their current session for the most up-to-date values
      // This avoids race conditions between prop updates and local state
      let effectiveInProgress = inProgressProp;
      if (isCurrentUser) {
        dateToCompare = CurrentSession.user.lastTugged;
        // active takes priority — always show if active is true
        effectiveInProgress = CurrentSession.user.active || inProgressProp;
      }

      const state = calculateLastTuggedState(
        dateToCompare,
        timezone,
        effectiveInProgress,
        isCurrentUser,
        maxUnits
      );

      // Only update state if values actually changed to prevent unnecessary re-renders/flickering
      setColor((prev) => (prev !== state.color ? state.color : prev));
      setDisplayString((prev) =>
        prev !== state.displayString ? state.displayString : prev
      );
      setActive((prev) => (prev !== state.active ? state.active : prev));

      // Set icon based on color (for non-active states)
      if (!state.active) {
        let newIcon: ElementType;
        if (state.color === "error") {
          newIcon = IconMoodPuzzled;
        } else if (state.color === "warning") {
          newIcon = IconMoodSmile;
        } else if (state.color === "success") {
          const dateToCompareMoment = moment(
            TimeUtils.inThisTimezone(dateToCompare, timezone)
          );
          const elapsed = moment().diff(dateToCompareMoment);
          const sixHours = moment.duration(6, "hours").asMilliseconds();
          newIcon = elapsed > sixHours ? IconMoodSmileBeam : IconMoodHappy;
        } else {
          newIcon = IconMoodPlus;
        }
        setIcon((prev: ElementType) => (prev !== newIcon ? newIcon : prev));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [date, inProgressProp, isCurrentUser, timezone, maxUnits]
  );

  function calculate() {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(calculateImmediate, 500);
  }

  // Recompute right away whenever the display inputs change -- notably maxUnits
  // flipping on a resize -- so the label updates without needing a reload.
  // (The interval effect below only keys off date/inProgress.)
  useEffect(() => {
    calculateImmediate();
  }, [calculateImmediate]);

  useEffect(() => {
    function handleUpdate() {
      if (isCurrentUser) {
        calculate();
      }
    }

    if (typeof window !== "undefined") {
      window.addEventListener("userUpdated", handleUpdate);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("userUpdated", handleUpdate);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCurrentUser]);

  useEffect(() => {
    if (date) {
      calculate();
      const intervalId = setInterval(
        calculate,
        isCurrentUser ? 5000 : TimeUtils.RefreshRate
      );

      return () => {
        clearInterval(intervalId);
      };
    } else {
      setColor("info");
      setDisplayString("New user!");
      setIcon(IconMoodPlus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, inProgressProp]);

  // Define a gentle pulse animation for the biceps icon
  const pulseAnimation = keyframes`
    0% {
      opacity: .9;
      transform: scale(1);
    }
    50% {
      opacity: 1;
      transform: scale(1.1);
    }
    100% {
      opacity: .9;
      transform: scale(1);
    }
  `;

  const renderedIcon = <Icon />;
  const pulsingBicepsIcon = (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        animation: `${pulseAnimation} 1.5s ease-in-out infinite`,
      }}
    >
      <LuBicepsFlexed size={24} color="white" />
    </Box>
  );

  if (displayString === "New User!") {
    return null;
  }

  return (
    <Tooltip title="Last Seen" arrow>
      <CustomChip
        icon={active ? pulsingBicepsIcon : renderedIcon}
        color={color}
        label={displayString}
        style={{ marginLeft: 5, marginRight: 0, ...style }}
        sx={{ "& .MuiChip-label": { overflow: "visible" } }}
      />
    </Tooltip>
  );
};

export default LastTugged;
